import React, { useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';

import { loadConnectAndInitialize, type StripeConnectInstance } from '@stripe/connect-js';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';

import { ErrorState } from '@/components/ui/error-state';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

import { useColorScheme } from '@/hooks/use-color-scheme';
import { CONNECT_FONTS, CONNECT_LOCALE, connectAppearance } from './appearance';
import { useStripeConnectBootstrap } from './use-stripe-connect-bootstrap';

/**
 * Provider do Connect para web (T4.4, plano seção 2.6): `@stripe/connect-js`
 * + `@stripe/react-connect-js`. Faz o bootstrap (`publishableKey` + primeira
 * sessão), monta o `StripeConnectInstance` uma única vez e só então libera
 * os filhos (`AccountOnboarding`, `NotificationBanner`, `AccountManagement`).
 * Ver `stripe-connect-provider.tsx` (implementação nativa — sem sufixo, ver
 * nota no `index.ts`) para o par nativo.
 */
export function StripeConnectProvider({ children }: { children: React.ReactNode }) {
  const { status, publishableKey, errorMessage, retry, fetchClientSecret } =
    useStripeConnectBootstrap();
  const scheme = useColorScheme();
  const schemeRef = useRef(scheme);
  schemeRef.current = scheme;

  const instance = useMemo<StripeConnectInstance | null>(() => {
    if (status !== 'ready' || !publishableKey) return null;
    return loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret,
      appearance: connectAppearance(schemeRef.current),
      fonts: CONNECT_FONTS,
      locale: CONNECT_LOCALE,
    });
    // `fetchClientSecret` é estável (useCallback); recriar a instância a
    // cada render descartaria a sessão em andamento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, publishableKey]);

  // Trocar o tema não recria a instância (perderia a sessão): só repinta.
  useEffect(() => {
    instance?.update({ appearance: connectAppearance(scheme) });
  }, [instance, scheme]);

  if (status === 'loading') {
    return (
      <View className="items-center justify-center py-10">
        <LoadingSpinner message="Abrindo..." />
      </View>
    );
  }

  if (status === 'error' || !instance) {
    return (
      <ErrorState
        variant="compact"
        title="Não foi possível abrir"
        message={errorMessage ?? undefined}
        onRetry={retry}
      />
    );
  }

  return (
    <ConnectComponentsProvider connectInstance={instance}>{children}</ConnectComponentsProvider>
  );
}
